import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Cookie, Settings } from 'lucide-react';

const CookieConsent = () => {
  const location = useLocation();
  const isOrderFlowPage =
    location.pathname === '/siparis-basarili' ||
    location.pathname === '/siparis-teyit' ||
    location.pathname === '/sepet' ||
    location.pathname === '/odeme';
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (isOrderFlowPage) return;
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after 1 second
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        console.error('Failed to parse cookie consent');
      }
    }
  }, [isOrderFlowPage]);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner || isOrderFlowPage) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/20 z-[9998] pointer-events-none" />

      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up">
        <div className="container mx-auto px-4 pb-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden max-w-5xl mx-auto">
            
            {/* Main Banner */}
            {!showSettings ? (
              <div className="p-6 md:p-8">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-[#D4AF37]" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-['Cormorant_Garamond'] font-bold text-[#1a1a1a] mb-2">
                      🍪 Çerez Kullanımı
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      Web sitemizde deneyiminizi iyileştirmek, site trafiğini analiz etmek ve 
                      kişiselleştirilmiş içerik sunmak için çerezler kullanıyoruz. 
                      Sitemizi kullanmaya devam ederek çerez kullanımını kabul etmiş olursunuz.
                      {' '}
                      <Link to="/legal/cerez-politikasi" className="text-[#D4AF37] hover:underline font-medium">
                        Çerez Politikamızı
                      </Link>
                      {' '}inceleyebilirsiniz.
                    </p>

                    {/* Buttons */}
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleAcceptAll}
                        className="px-6 py-2.5 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8941F] 
                                 transition-all duration-300 font-medium text-sm shadow-lg hover:shadow-xl"
                      >
                        Tümünü Kabul Et
                      </button>
                      <button
                        onClick={handleRejectAll}
                        className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 
                                 transition-all duration-300 font-medium text-sm"
                      >
                        Tümünü Reddet
                      </button>
                      <button
                        onClick={() => setShowSettings(true)}
                        className="px-6 py-2.5 bg-white border-2 border-[#D4AF37] text-[#D4AF37] 
                                 rounded-lg hover:bg-[#D4AF37] hover:text-white transition-all duration-300 
                                 font-medium text-sm flex items-center gap-2"
                      >
                        <Settings className="w-4 h-4" />
                        Ayarlar
                      </button>
                    </div>
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={handleRejectAll}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 
                             transition-colors flex-shrink-0"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>
            ) : (
              /* Settings Panel */
              <div className="p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-['Cormorant_Garamond'] font-bold text-[#1a1a1a]">
                    Çerez Tercihleri
                  </h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Necessary Cookies */}
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <span className="text-green-600 font-bold">✓</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#1a1a1a]">Gerekli Çerezler</h4>
                          <p className="text-xs text-gray-500">Her zaman aktif</p>
                        </div>
                      </div>
                      <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 ml-13">
                      Web sitesinin temel işlevlerini sağlamak için gereklidir. Oturum yönetimi, 
                      güvenlik ve sepet işlemleri için kullanılır.
                    </p>
                  </div>

                  {/* Functional Cookies */}
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <span className="text-blue-600 font-bold">⚙️</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#1a1a1a]">İşlevsel Çerezler</h4>
                          <p className="text-xs text-gray-500">Önerilen</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePreference('functional')}
                        className={`w-12 h-6 rounded-full transition-colors duration-300 flex items-center px-1 ${
                          preferences.functional ? 'bg-[#D4AF37] justify-end' : 'bg-gray-300 justify-start'
                        }`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 ml-13">
                      Dil tercihleri, bölge ayarları gibi kişiselleştirilmiş özellikleri hatırlar.
                    </p>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <span className="text-purple-600 font-bold">📊</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#1a1a1a]">Analitik Çerezler</h4>
                          <p className="text-xs text-gray-500">İsteğe bağlı</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePreference('analytics')}
                        className={`w-12 h-6 rounded-full transition-colors duration-300 flex items-center px-1 ${
                          preferences.analytics ? 'bg-[#D4AF37] justify-end' : 'bg-gray-300 justify-start'
                        }`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 ml-13">
                      Site kullanımını analiz eder ve performansı iyileştirmemize yardımcı olur.
                    </p>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <span className="text-orange-600 font-bold">🎯</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#1a1a1a]">Pazarlama Çerezleri</h4>
                          <p className="text-xs text-gray-500">İsteğe bağlı</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePreference('marketing')}
                        className={`w-12 h-6 rounded-full transition-colors duration-300 flex items-center px-1 ${
                          preferences.marketing ? 'bg-[#D4AF37] justify-end' : 'bg-gray-300 justify-start'
                        }`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 ml-13">
                      İlgi alanlarınıza uygun reklamlar göstermek için kullanılır.
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 px-6 py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8941F] 
                             transition-all duration-300 font-medium shadow-lg hover:shadow-xl"
                  >
                    Tercihleri Kaydet
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 
                             transition-all duration-300 font-medium"
                  >
                    Tümünü Kabul Et
                  </button>
                </div>

                <p className="text-xs text-gray-500 text-center mt-4">
                  Daha fazla bilgi için{' '}
                  <Link to="/legal/cerez-politikasi" className="text-[#D4AF37] hover:underline">
                    Çerez Politikamızı
                  </Link>
                  {' '}inceleyebilirsiniz.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </>
  );
};

export default CookieConsent;
